import { useState, useRef, useCallback, useEffect } from "react";
import {
  getSpeechRecognition,
  type SpeechRecognitionEvent,
  type SpeechRecognitionInstance,
} from "../journal/speechRecognition";

export interface VoiceRecorderState {
  isRecording: boolean;
  elapsed: number;
  /** Final recording duration in seconds (preserved after stop). */
  duration: number;
  audioBlob: Blob | null;
  audioUrl: string | null;
  /** Live on-device transcript (GDPR-safe). */
  transcript: string;
  isTranscriptionSupported: boolean;
  start: () => Promise<void>;
  /** Stop recording and return the finished Blob. */
  stop: () => Promise<Blob | null>;
  clear: () => void;
  error: string | null;
}

export const useVoiceRecorder = (): VoiceRecorderState => {
  const [isRecording, setIsRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [duration, setDuration] = useState(0);
  const elapsedRef = useRef(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [transcript, setTranscript] = useState("");

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const blobResolverRef = useRef<((blob: Blob | null) => void) | null>(null);
  const recognitionRef = useRef<SpeechRecognitionInstance | null>(null);

  const isTranscriptionSupported = !!getSpeechRecognition();

  // Clean up object URLs on unmount
  useEffect(() => {
    return () => {
      if (audioUrl) URL.revokeObjectURL(audioUrl);
      stopStream();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stopStream = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  const startTimer = () => {
    elapsedRef.current = 0;
    setElapsed(0);
    timerRef.current = setInterval(() => {
      elapsedRef.current += 1;
      setElapsed(elapsedRef.current);
    }, 1000);
  };

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const start = useCallback(async () => {
    setError(null);

    // Revoke previous recording URL
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
      setAudioUrl(null);
    }
    setAudioBlob(null);
    setTranscript("");

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Start live transcription
      const SpeechRecognitionCtor = getSpeechRecognition();
      if (SpeechRecognitionCtor) {
        const recognition = new SpeechRecognitionCtor();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = "en-GB"; // adapt to user locale if needed
        let finalised = "";
        recognition.onresult = (e: SpeechRecognitionEvent) => {
          let interim = "";
          for (let i = e.resultIndex; i < e.results.length; i++) {
            const result = e.results[i];
            if (result.isFinal) {
              finalised += result[0].transcript + " ";
            } else {
              interim += result[0].transcript;
            }
          }
          setTranscript((finalised + interim).trim());
        };
        recognition.onerror = () => {
          // Non-fatal — audio still records even if transcription fails
        };
        recognition.onend = () => {
          // Browser may auto-stop after silence; we don't restart because
          // the user may simply be pausing. The finalised text is kept.
        };
        recognition.start();
        recognitionRef.current = recognition;
      }

      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/webm")
        ? "audio/webm"
        : ""; // let browser pick

      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, {
          type: recorder.mimeType || "audio/webm",
        });
        const url = URL.createObjectURL(blob);
        // Capture final duration from ref (immune to React batching)
        setDuration(elapsedRef.current);
        setAudioBlob(blob);
        setAudioUrl(url);
        stopStream();
        stopTimer();
        blobResolverRef.current?.(blob);
        blobResolverRef.current = null;
      };

      recorder.start();
      setIsRecording(true);
      startTimer();
    } catch (err: unknown) {
      console.error("Microphone access error:", err);
      if (err instanceof DOMException && err.name === "NotAllowedError") {
        setError("Microphone access was denied. Please allow it in your browser settings.");
      } else {
        setError("Could not access microphone.");
      }
    }
  }, [audioUrl]);

  const stopRecognition = () => {
    try { recognitionRef.current?.stop(); } catch { /* already stopped */ }
    recognitionRef.current = null;
  };

  const stop = useCallback((): Promise<Blob | null> => {
    stopRecognition();
    if (mediaRecorderRef.current?.state !== "recording") {
      // Already stopped — resolve immediately with whatever blob we have.
      setIsRecording(false);
      return Promise.resolve(audioBlob);
    }
    setIsRecording(false);
    const p = new Promise<Blob | null>((resolve) => {
      blobResolverRef.current = resolve;
      mediaRecorderRef.current!.stop();
    });
    return p;
  }, [audioBlob]);

  const clear = useCallback(() => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioBlob(null);
    setAudioUrl(null);
    setElapsed(0);
    elapsedRef.current = 0;
    setDuration(0);
    setError(null);
    setTranscript("");
    blobResolverRef.current = null;
    stopRecognition();
  }, [audioUrl]);

  return {
    isRecording, elapsed, duration, audioBlob, audioUrl,
    transcript, isTranscriptionSupported,
    start, stop, clear, error,
  };
};
